import { useState, useRef, useEffect } from "react";

import {
  wrapText
} from "../helpers/canvasHelpers";

/**
 * 🎯 Handles:
 * - Image capture from video
 * - Video recording with overlay
 * - Exact UI-like crop (object-fit: cover)
 * - Live GPS Address
 * - Pause / Resume / Stop
 */

export const useMediaCapture = (
  remoteVideoRef,
  insuredInfo,
  address,
  roomId,
  isReconnecting = false
) => {

  const [capturedImages, setCapturedImages] = useState([]);
  const [recordedVideos, setRecordedVideos] = useState([]);

  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);

  const [progress, setProgress] = useState(0);

  // =====================================================
  // REFS
  // =====================================================

  const mediaRecorderRef = useRef(null);

  const intervalRef = useRef(null);

  const animationRef = useRef(null);

  const canvasRef = useRef(null);

  const ctxRef = useRef(null);

  const peerIdRef = useRef("");

  const insuredInfoRef = useRef({});

  const addressCache = useRef({});

  const timeRef = useRef(0);

  const reconnectRef = useRef(false);

  const apiKey ="AIzaSyAbaQ3ldMRUYmxt39TdasRdU-rtPLyZVhc";

  // =====================================================
  // SYNC REFS
  // =====================================================

  useEffect(() => {
    peerIdRef.current = roomId;
  }, [roomId]);

  useEffect(() => {
    insuredInfoRef.current = insuredInfo;
  }, [insuredInfo]);

  useEffect(() => {
    reconnectRef.current = isReconnecting;
  }, [isReconnecting]);

  useEffect(() => {

  const loadMap = async () => {

    const data =
      insuredInfoRef.current;

    const lat = data?.lat;
    const lng = data?.lng;

    if (!lat || !lng) return;

    const img =
      await getMapImage(
        lat,
        lng
      );

    mapImageRef.current = img;
  };

  loadMap();

}, [
  insuredInfo?.lat,
  insuredInfo?.lng
]);

  // =====================================================
  // FETCH ADDRESS FROM LAT LNG
  // =====================================================

  const getAddressFromLatLng = async (
    lat,
    lng
  ) => {

  if (!lat || !lng) {
    return "Fetching location...";
  }

  const key =
    `${lat.toFixed(5)},${lng.toFixed(5)}`;

  // CACHE
  if (addressCache.current[key]) {
    return addressCache.current[key];
  }

  try {

    

    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`
    );

    const data =
      await response.json();

    if (
      data.status === "OK" &&
      data.results?.length > 0
    ) {

      const formattedAddress =
        data.results[0]
          .formatted_address;

      addressCache.current[key] =
        formattedAddress;

      return formattedAddress;
    }

    return "Location unavailable";

  } catch (err) {

    return "Location unavailable";
  }
  };

  // =====================================================
  // SAFE ROUND RECT
  // =====================================================

const drawRoundRect = (
  ctx,
  x,
  y,
  width,
  height,
  radius,
  fillColor = null,
  strokeColor = null
) => {

  ctx.save();

  ctx.beginPath();

  ctx.moveTo(x + radius, y);

  ctx.arcTo(
    x + width,
    y,
    x + width,
    y + height,
    radius
  );

  ctx.arcTo(
    x + width,
    y + height,
    x,
    y + height,
    radius
  );

  ctx.arcTo(
    x,
    y + height,
    x,
    y,
    radius
  );

  ctx.arcTo(
    x,
    y,
    x + width,
    y,
    radius
  );

  ctx.closePath();

  // FILL

  if (fillColor) {

    ctx.fillStyle = fillColor;

    ctx.fill();
  }

  // STROKE

  if (strokeColor) {

    ctx.strokeStyle = strokeColor;

    ctx.stroke();
  }

  ctx.restore();
};

  // =====================================================
    // ROUND RECT PATH
    // =====================================================

    const roundRectPath = (
      ctx,
      x,
      y,
      width,
      height,
      radius,
      tl = true,
      tr = true,
      br = true,
      bl = true
    ) => {

      ctx.beginPath();

      ctx.moveTo(
        x + (tl ? radius : 0),
        y
      );

      ctx.lineTo(
        x + width - (tr ? radius : 0),
        y
      );

      if (tr) {
        ctx.quadraticCurveTo(
          x + width,
          y,
          x + width,
          y + radius
        );
      }

      ctx.lineTo(
        x + width,
        y + height - (br ? radius : 0)
      );

      if (br) {
        ctx.quadraticCurveTo(
          x + width,
          y + height,
          x + width - radius,
          y + height
        );
      }

      ctx.lineTo(
        x + (bl ? radius : 0),
        y + height
      );

      if (bl) {
        ctx.quadraticCurveTo(
          x,
          y + height,
          x,
          y + height - radius
        );
      }

      ctx.lineTo(
        x,
        y + (tl ? radius : 0)
      );

      if (tl) {
        ctx.quadraticCurveTo(
          x,
          y,
          x + radius,
          y
        );
      }

      ctx.closePath();
    };

  // =====================================================
  // DRAW VIDEO LIKE object-fit: cover
  // =====================================================

  const drawVideoCover = (
    ctx,
    video,
    canvas
  ) => {

    const vw = video.videoWidth;
    const vh = video.videoHeight;

    const cw = canvas.width;
    const ch = canvas.height;

    const videoAspect = vw / vh;

    const canvasAspect = cw / ch;

    let sx = 0;
    let sy = 0;

    let sWidth = vw;
    let sHeight = vh;

    // SAME AS CSS object-fit: cover

    if (videoAspect > canvasAspect) {

      sWidth = vh * canvasAspect;

      sx = (vw - sWidth) / 2;

    } else {

      sHeight = vw / canvasAspect;

      sy = (vh - sHeight) / 2;
    }

    ctx.drawImage(
      video,
      sx,
      sy,
      sWidth,
      sHeight,
      0,
      0,
      cw,
      ch
    );
  };

  // =====================================================
  // DRAW OVERLAY
  // =====================================================

//   const drawOverlay = (
//   ctx,
//   vw,
//   vh,
//   location,
//   forcedTime = null
// ) => {

//   const data = insuredInfoRef.current;

//   const time =
//     forcedTime ||
//     data?.time ||
//     new Date().toLocaleString();

//   const aid =
//     peerIdRef.current || "----";

//   // =================================================
//   // SCALE
//   // =================================================

//   const scale = vw / 390;

//   // Smaller fonts
//   const liveFont = 8 * scale;
//   const timeFont = 8 * scale;
//   const locationFont = 8 * scale;
//   const aidFont = 10 * scale;

//   // =================================================
//   // LIVE BADGE
//   // =================================================

//   ctx.font = `${liveFont}px sans-serif`;

//   const liveTextWidth =
//     ctx.measureText("LIVE").width;

//   const liveWidth =
//     liveTextWidth + (26 * scale);

//   const liveHeight =
//     20 * scale;

//   const liveX = 12 * scale;
//   const liveY = 12 * scale;

//   ctx.fillStyle =
//     "rgba(0,0,0,0.40)";

//   roundRect(
//     ctx,
//     liveX,
//     liveY,
//     liveWidth,
//     liveHeight,
//     10 * scale,
//     true
//   );

//   // RED DOT

//   ctx.beginPath();

//   ctx.fillStyle = "#ff3b30";

//   ctx.arc(
//     liveX + 8 * scale,
//     liveY + liveHeight / 2,
//     3 * scale,
//     0,
//     Math.PI * 2
//   );

//   ctx.fill();

//   // LIVE TEXT

//   ctx.fillStyle = "#fff";

//   ctx.textBaseline = "middle";
//   ctx.textAlign = "left";

//   ctx.fillText(
//     "LIVE",
//     liveX + 16 * scale,
//     liveY + liveHeight / 2
//   );

//   // =================================================
//   // LOCATION LINES
//   // =================================================

//   ctx.font =
//     `${locationFont}px sans-serif`;

//   const maxTextWidth =
//     180 * scale;

//   const lineHeight =
//     11 * scale;

//   const words =
//     location.split(" ");

//   let lines = [];
//   let line = "";

//   for (let n = 0; n < words.length; n++) {

//     const testLine =
//       line + words[n] + " ";

//     const metrics =
//       ctx.measureText(testLine);

//     if (
//       metrics.width > maxTextWidth &&
//       n > 0
//     ) {

//       lines.push(line);

//       line =
//         words[n] + " ";

//     } else {

//       line = testLine;
//     }
//   }

//   lines.push(line);

//   // =================================================
//   // INFO BOX
//   // =================================================

//   const boxPadding =
//     10 * scale;

//   const boxWidth =
//     maxTextWidth +
//     (boxPadding * 2);

//   // Reduced height
//   const boxHeight =
//     (58 * scale) +
//     (lines.length * lineHeight);

//   const boxX =
//     vw - boxWidth - (12 * scale);

//   const boxY =
//     12 * scale;

//   ctx.fillStyle =
//     "rgba(0,0,0,0.40)";

//   roundRect(
//     ctx,
//     boxX,
//     boxY,
//     boxWidth,
//     boxHeight,
//     12 * scale,
//     true
//   );

//   // =================================================
//   // TIME CHIP
//   // =================================================

//   ctx.font =
//     `${timeFont}px sans-serif`;

//   const timeTextWidth =
//     ctx.measureText(time).width;

//   const chipWidth =
//     timeTextWidth +
//     (16 * scale);

//   const chipHeight =
//     18 * scale;

//   const chipX =
//     boxX +
//     boxWidth -
//     chipWidth -
//     (8 * scale);

//   const chipY =
//     boxY + (8 * scale);

//   ctx.fillStyle =
//     "rgba(255,255,255,0.12)";

//   roundRect(
//     ctx,
//     chipX,
//     chipY,
//     chipWidth,
//     chipHeight,
//     10 * scale,
//     true
//   );

//   ctx.fillStyle = "#fff";

//   ctx.textAlign = "center";
//   ctx.textBaseline = "middle";

//   ctx.fillText(
//     time,
//     chipX + chipWidth / 2,
//     chipY + chipHeight / 2
//   );

//   // =================================================
//   // LOCATION
//   // =================================================

//   ctx.fillStyle = "#fff";

//   ctx.font =
//     `${locationFont}px sans-serif`;

//   ctx.textAlign = "right";
//   ctx.textBaseline = "top";

//   const locationStartY =
//     boxY + (34 * scale);

//   lines.forEach((lineText, index) => {

//     ctx.fillText(
//       lineText,
//       boxX +
//         boxWidth -
//         (10 * scale),
//       locationStartY +
//         (index * lineHeight)
//     );
//   });

//   // =================================================
//   // AID
//   // =================================================

//   ctx.fillStyle = "#ff3b3b";

//   ctx.font =
//     `600 ${aidFont}px sans-serif`;

//   ctx.textAlign = "right";
//   ctx.textBaseline = "bottom";

//   ctx.fillText(
//     `AID: ${aid}`,
//     boxX +
//       boxWidth -
//       (10 * scale),
//     boxY +
//       boxHeight -
//       (8 * scale)
//   );

//   // RESET

//   ctx.textAlign = "left";
//   };

  // =====================================================
  // DRAW COMPLETE FRAME
  // =====================================================

  // const drawFrame = async (
  //   ctx,
  //   canvas,
  //   video,
  //   forcedTime = null
  // ) => {

  //   const data =
  //     insuredInfoRef.current;

  //   const lat = data?.lat;
  //   const lng = data?.lng;

  //   // GET COMPLETE ADDRESS
  //   const location =
  //     await getAddressFromLatLng(
  //       lat,
  //       lng
  //     );

  //   // DRAW VIDEO
  //   drawVideoCover(
  //     ctx,
  //     video,
  //     canvas
  //   );

  //   // DRAW OVERLAY
  //   drawOverlay(
  //     ctx,
  //     canvas.width,
  //     canvas.height,
  //     location,
  //     forcedTime
  //   );
  // };

  // =====================================================
// LOAD IMAGE
// =====================================================

// const loadImage = (src) => {

//   return new Promise((resolve, reject) => {

//     const img = new Image();

//     img.crossOrigin = "anonymous";

//     img.onload = () => resolve(img);

//     img.onerror = reject;

//     img.src = src;

//   });
// };

// =====================================================
// MAP IMAGE CACHE
// =====================================================

const mapImageRef = useRef({});

// =====================================================
// LOAD IMAGE
// =====================================================

const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.crossOrigin = "anonymous";

    img.onload = () => resolve(img);

    img.onerror = reject;

    img.src = src;
  });
};

// =====================================================
// GET MAP IMAGE
// =====================================================

const getMapImage = async (lat, lng) => {
  if (!lat || !lng) return null;

  const key = `${lat},${lng}`;

  // CACHE
  if (mapImageRef.current[key]) {
    return mapImageRef.current[key];
  }

  try {
    const mapUrl =
      `https://maps.googleapis.com/maps/api/staticmap` +
      `?center=${lat},${lng}` +
      `&zoom=16` +
      `&size=400x400` +
      `&maptype=roadmap` +
      `&markers=color:red%7C${lat},${lng}` +
      `&key=${apiKey}`;

    const img = await loadImage(mapUrl);

    mapImageRef.current[key] = img;

    return img;
  } catch (err) {
    console.log("Map image failed");

    return null;
  }
};

// =====================================================
// DRAW OVERLAY
// =====================================================

const drawOverlay = (
  ctx,
  vw,
  vh,
  location,
  mapImg,
  forcedTime = null
) => {

  const data =
    insuredInfoRef.current || {};

  const lat =
    data?.lat || "--";

  const lng =
    data?.lng || "--";

  const time =
    forcedTime ||
    data?.time ||
    new Date().toLocaleString();

  const aid =
    peerIdRef.current || "----";

  // ============================================
  // SCALE
  // ============================================

  const scale = vw / 390;

  // ============================================
  // BOX
  // ============================================

  const boxWidth =
    255 * scale;

  const boxHeight =
    95 * scale;

  const mapWidth =
    96 * scale;

  const radius =
    14 * scale;

  const boxX =
    vw - boxWidth - (12 * scale);

  const boxY =
    12 * scale;

  // ============================================
  // MAIN GLASS BOX
  // ============================================

  ctx.save();

  roundRectPath(
    ctx,
    boxX,
    boxY,
    boxWidth,
    boxHeight,
    radius
  );

  ctx.clip();

  // ============================================
  // MAP AREA
  // ============================================

  if (mapImg) {

    ctx.globalAlpha = 1;

    ctx.drawImage(
      mapImg,
      boxX,
      boxY,
      mapWidth,
      boxHeight
    );

  } else {

    ctx.fillStyle =
      "#1f2937";

    ctx.fillRect(
      boxX,
      boxY,
      mapWidth,
      boxHeight
    );

    ctx.fillStyle =
      "rgba(255,255,255,0.75)";

    ctx.font =
      `${9 * scale}px sans-serif`;

    ctx.textAlign =
      "center";

    ctx.textBaseline =
      "middle";

    ctx.fillText(
      "Loading map...",
      boxX + (mapWidth / 2),
      boxY + (boxHeight / 2)
    );
  }

  // ============================================
  // INFO PANEL GLASS
  // ============================================

  ctx.fillStyle =
    "rgba(0,0,0,0.45)";

  ctx.fillRect(
    boxX + mapWidth,
    boxY,
    boxWidth - mapWidth,
    boxHeight
  );

  ctx.restore();

  ctx.globalAlpha = 1;

  // ============================================
  // DIVIDER
  // ============================================

  ctx.strokeStyle =
    "rgba(255,255,255,0.08)";

  ctx.lineWidth = 1;

  ctx.beginPath();

  ctx.moveTo(
    boxX + mapWidth,
    boxY
  );

  ctx.lineTo(
    boxX + mapWidth,
    boxY + boxHeight
  );

  ctx.stroke();

  // ============================================
  // LIVE BADGE
  // ============================================

  // LIVE BADGE

  ctx.beginPath();

  ctx.roundRect(
    boxX + (6 * scale),
    boxY + (6 * scale),
    30 * scale,
    13 * scale,
    999
  );

  ctx.fillStyle = "#ff3b3b";

  ctx.fill();

  ctx.fillStyle =
    "#ffffff";

  ctx.font =
    `700 ${8 * scale}px sans-serif`;

  ctx.textAlign =
    "center";

  ctx.textBaseline =
    "middle";

  ctx.fillText(
    "LIVE",
    boxX + (21 * scale),
    boxY + (12.5 * scale)
  );

  // ============================================
  // INFO AREA
  // ============================================

  const infoX =
    boxX +
    mapWidth +
    (10 * scale);

  // ============================================
  // TIME CHIP
  // ============================================

  ctx.font =
    `${8.5 * scale}px sans-serif`;

  const chipWidth =
    ctx.measureText(time).width +
    (16 * scale);

    ctx.beginPath();

    ctx.roundRect(
      boxX + (6 * scale),
      boxY + (6 * scale),
      30 * scale,
      13 * scale,
      999
    );

    ctx.fillStyle = "#211f1f56";

    ctx.fill();

  ctx.fillStyle =
    "#ffffff";

  ctx.textAlign =
    "left";

  ctx.textBaseline =
    "middle";

  ctx.fillText(
    time,
    infoX + (8 * scale),
    boxY + (15.5 * scale)
  );

  // ============================================
  // LOCATION
  // ============================================

  ctx.fillStyle =
    "#ffffff";

  ctx.font =
    `${10 * scale}px sans-serif`;

  const maxTextWidth =
    boxWidth -
    mapWidth -
    (20 * scale);

  const words =
    (location || "")
      .split(" ");

  const lines = [];

  let line = "";

  for (const word of words) {

    const testLine =
      line + word + " ";

    if (
      ctx.measureText(testLine)
        .width > maxTextWidth
    ) {

      if (line) {

        lines.push(
          line.trim()
        );
      }

      line =
        word + " ";

    } else {

      line = testLine;
    }
  }

  if (line) {

    lines.push(
      line.trim()
    );
  }

  ctx.textBaseline =
    "top";

  lines
    .slice(0, 3)
    .forEach(
      (
        lineText,
        index
      ) => {

        ctx.fillText(
          lineText,
          infoX,
          boxY +
            (34 * scale) +
            (
              index *
              12 *
              scale
            )
        );
      }
    );

  // ============================================
  // LAT LNG
  // ============================================

  ctx.fillStyle =
    "rgba(255,255,255,0.60)";

  ctx.font =
    `${8 * scale}px monospace`;

  ctx.fillText(
    `${lat}, ${lng}`,
    infoX,
    boxY + (72 * scale)
  );

  // ============================================
  // AID
  // ============================================

  ctx.fillStyle =
    "#ff5252";

  ctx.font =
    `700 ${9 * scale}px sans-serif`;

  ctx.fillText(
    `AID: ${aid}`,
    infoX,
    boxY + (85 * scale)
  );

  // ============================================
  // RESET
  // ============================================

  ctx.textAlign =
    "left";

  ctx.textBaseline =
    "alphabetic";

  ctx.globalAlpha = 1;
};

// =====================================================
// DRAW FRAME
// =====================================================

const drawFrame = async (
  ctx,
  canvas,
  video,
  forcedTime = null
) => {

  if (!video) return;

  const data =
    insuredInfoRef.current || {};

  const lat = data?.lat;
  const lng = data?.lng;

  // CLEAR
  ctx.clearRect(
    0,
    0,
    canvas.width,
    canvas.height
  );

  // DRAW VIDEO
  drawVideoCover(
    ctx,
    video,
    canvas
  );

  // ADDRESS
  const location =
    await getAddressFromLatLng(
      lat,
      lng
    );

  // MAP IMAGE
  const mapImage =
    await getMapImage(
      lat,
      lng
    );

  // DRAW UI
  drawOverlay(
    ctx,
    canvas.width,
    canvas.height,
    location,
    mapImage,
    forcedTime
  );
};

  // =====================================================
  // IMAGE CAPTURE
  // =====================================================

  const handleCapture = async () => {

    if (reconnectRef.current) {
      return;
    }

    const video =
      remoteVideoRef.current;

    if (!video) return;

    const canvas =
      document.createElement("canvas");

    const ctx =
      canvas.getContext("2d");

    // EXACT SAME AS LIVE VIEW
    const aspect =
      video.videoWidth /
      video.videoHeight;

    const targetWidth = 1280;

    const targetHeight =
      targetWidth / aspect;

    canvas.width =
      targetWidth;

    canvas.height =
      targetHeight;

    const captureTime =
      new Date().toLocaleString();
    await drawFrame(
      ctx,
      canvas,
      video,
      captureTime
    );

    const image =
      canvas.toDataURL(
        "image/jpeg",
        1
      );

    setCapturedImages(prev => [
      image,
      ...prev
    ]);
  };

  // =====================================================
  // VIDEO RECORDING
  // =====================================================

  const handleVideoRecord = () => {

    if (reconnectRef.current) {
      return;
    }

    const video =
      remoteVideoRef.current;

    if (!video) return;

    const canvas =
      document.createElement("canvas");

    const ctx =
      canvas.getContext("2d");

    // EXACT SAME AS LIVE VIEW
    canvas.width =
      video.clientWidth * 2;

    canvas.height =
      video.clientHeight * 2;

    canvasRef.current = canvas;

    ctxRef.current = ctx;

    const stream =
      canvas.captureStream(30);

    // ADD AUDIO
    const remoteStream =
      video.srcObject;

    remoteStream
      ?.getAudioTracks()
      .forEach(track => {
        stream.addTrack(track);
      });

    const recorder =
      new MediaRecorder(stream, {
        mimeType:
          "video/webm;codecs=vp9"
      });

    mediaRecorderRef.current =
      recorder;

    let chunks = [];

    recorder.ondataavailable = e => {

      if (e.data.size > 0) {
        chunks.push(e.data);
      }
    };

    recorder.onstop = () => {

      const blob = new Blob(
        chunks,
        {
          type: "video/webm"
        }
      );

      const url =
        URL.createObjectURL(blob);

      setRecordedVideos(prev => [
        url,
        ...prev
      ]);
    };

    // =================================================
    // DRAW LOOP
    // =================================================

    // const draw = async () => {

    //   if (
    //     recorder.state !== "recording"
    //   ) return;

    //   await drawFrame(
    //     ctx,
    //     canvas,
    //     video
    //   );

    //   animationRef.current =
    //     requestAnimationFrame(draw);
    // };

    

    const draw = async () => {

      if (
        mediaRecorderRef.current?.state === "inactive"
      ) return;

      // STOP DRAWING DURING RECONNECT
      if (reconnectRef.current) {

        animationRef.current =
          requestAnimationFrame(draw);

        return;
      }

      await drawFrame(
        ctx,
        canvas,
        video
      );

      animationRef.current =
        requestAnimationFrame(draw);
    };

    recorder.start();

    setRecording(true);

    setPaused(false);

    timeRef.current = 0;

    draw();

    intervalRef.current =
      setInterval(() => {

        if (
          mediaRecorderRef.current
            ?.state !== "recording"
        ) return;

        // FREEZE TIMER DURING RECONNECT
        if (reconnectRef.current) return;

        if (paused) return;

        timeRef.current += 1;

        setProgress(
          (timeRef.current / 120) * 100
        );

        if (
          timeRef.current >= 120
        ) {
          stopRecording();
        }

      }, 1000);
  };

  // =====================================================
  // PAUSE
  // =====================================================

  const pauseRecording = () => {

    mediaRecorderRef.current?.pause();

    setPaused(true);
  };

  // =====================================================
  // RESUME
  // =====================================================

  const resumeRecording = () => {

    mediaRecorderRef.current?.resume();

    setPaused(false);

    const video =
      remoteVideoRef.current;

    const canvas =
      canvasRef.current;

    const ctx = ctxRef.current;

    const draw = async () => {

      if (
        mediaRecorderRef.current
          ?.state !== "recording"
      ) return;

      await drawFrame(
        ctx,
        canvas,
        video
      );

      animationRef.current =
        requestAnimationFrame(draw);
    };

    draw();
  };

  const drawPauseOverlay = (
    ctx,
    vw,
    vh
  ) => {

    // DARK LAYER
    ctx.fillStyle =
      "rgba(0,0,0,0.45)";

    ctx.fillRect(
      0,
      0,
      vw,
      vh
    );

    // TEXT
    ctx.fillStyle = "#fff";

    ctx.font =
      `700 ${vw / 18}px sans-serif`;

    ctx.textAlign = "center";

    ctx.textBaseline = "middle";

    ctx.fillText(
      "PAUSED",
      vw / 2,
      vh / 2
    );

    // SUB TEXT
    ctx.font =
      `400 ${vw / 35}px sans-serif`;

    ctx.fillStyle =
      "rgba(255,255,255,0.85)";

    ctx.fillText(
      "Recording paused",
      vw / 2,
      (vh / 2) + (vw / 18)
    );

    ctx.textAlign = "left";
  };

  // =====================================================
  // STOP
  // =====================================================

  const stopRecording = () => {

    mediaRecorderRef.current?.stop();

    clearInterval(
      intervalRef.current
    );

    cancelAnimationFrame(
      animationRef.current
    );

    setRecording(false);

    setPaused(false);

    setProgress(0);
  };

  useEffect(() => {

  // AUTO PAUSE
  if (
    isReconnecting &&
    recording &&
    !paused
  ) {

    console.log(
      "⏸ Auto pause recording"
    );

    mediaRecorderRef.current?.pause();

    cancelAnimationFrame(
      animationRef.current
    );

    setPaused(true);
  }

  // AUTO RESUME
  if (
    !isReconnecting &&
    recording &&
    paused
  ) {

    console.log(
      "▶️ Auto resume recording"
    );

    mediaRecorderRef.current?.resume();

    setPaused(false);

    const video =
      remoteVideoRef.current;

    const canvas =
      canvasRef.current;

    const ctx =
      ctxRef.current;

    const draw = async () => {

      if (
        mediaRecorderRef.current?.state === "inactive"
      ) return;

      // DRAW VIDEO FRAME
      await drawFrame(
        ctx,
        canvas,
        video
      );

      // SHOW PAUSE OVERLAY
      if (
        mediaRecorderRef.current?.state === "paused"
      ) {

        drawPauseOverlay(
          ctx,
          canvas.width,
          canvas.height
        );
      }

      animationRef.current =
        requestAnimationFrame(draw);
    };

    draw();
  }

}, [
  isReconnecting,
  recording,
  paused
]);

  // =====================================================
  // RETURN
  // =====================================================

  return {

    capturedImages,

    recordedVideos,

    recording,

    paused,

    progress,

    handleCapture,

    handleVideoRecord,

    pauseRecording,

    resumeRecording,

    stopRecording,
  };
};